import {
  CaseAppContextProvider,
  type CaseAppContextValue,
} from '@repo/ccc-case-app-lib/src/context/AppContext/CaseAppContext.tsx'
import { composeLoginPathWithRedirectUrl } from '@repo/internal-tools-app-lib/src/lib/composeLoginPathWithRedirectUrl.ts'
import { ErrorPage } from '@repo/internal-tools-app-lib/src/lib/ErrorPage.tsx'
import { composeBasePath } from '@repo/internal-tools-ts-utils/src/lib/composeBasePath.ts'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRootRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { DevTools as JotaiDevTools } from 'jotai-devtools'
import { useCallback } from 'react'
import {
  ENV_INTERNAL_BFF_URL,
  ENV_VITE_BASE_PATH,
  ENV_VITE_DEV,
} from '../env.ts'

const getEnabledDevtools = () => {
  const searchParams = new URLSearchParams(window.location.search)
  // If you wish to enable devtools UI, add `?devtools=jotai,router,query` to the URL.
  // You can choose to enable each one i.e. `?devtools=jotai`
  return (searchParams.get('devtools') ?? '').split(',')
}

if (ENV_VITE_DEV && getEnabledDevtools().includes('jotai')) {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  import('jotai-devtools/styles.css')
}

export const Route = createRootRoute({
  notFoundComponent: () => <ErrorPage code="404" />,
  component: RootComponent,
})

const PROCESSED_BASE_PATH = composeBasePath(ENV_VITE_BASE_PATH)

function RootComponent() {
  const enabledDevTools = getEnabledDevtools()
  const navigate = useNavigate()

  /**
   * Redirect to the login page with client-side routing when an auth error occurs.
   */
  const authErrorRedirect: CaseAppContextValue['authErrorRedirect'] =
    useCallback(() => {
      // Correct the loginUrl with the currentUrl as redirectUrl
      // because the initialContext might not be updated.
      return navigate({
        to: composeLoginPathWithRedirectUrl(PROCESSED_BASE_PATH),
      })
    }, [navigate])

  return (
    <CaseAppContextProvider
      basePath={ENV_VITE_BASE_PATH}
      baseApiUrl={ENV_INTERNAL_BFF_URL}
      processedBasePath={PROCESSED_BASE_PATH}
      authErrorRedirect={authErrorRedirect}
    >
      <Outlet />
      {enabledDevTools.includes('jotai') && <JotaiDevTools />}
      {enabledDevTools.includes('router') && (
        <TanStackRouterDevtools initialIsOpen={false} position="bottom-right" />
      )}
      {enabledDevTools.includes('query') && (
        <ReactQueryDevtools
          initialIsOpen={false}
          position="bottom"
          buttonPosition="bottom-right"
        />
      )}
    </CaseAppContextProvider>
  )
}
